import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	EventEmitter,
	OnInit,
	Output,
	ViewChild,
	inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper, MatStep, MatStepContent } from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { AppDialogComponent } from 'src/app/components/app-dialog/app-dialog.component';
import { PhoneNumber } from 'src/app/models/data/user';
import { ApiService } from 'src/app/services/api.services';
import { AuthService } from 'src/app/services/auth.service';
import { MatCard } from '@angular/material/card';
import { NgClass } from '@angular/common';
import { CompleteRegistrationComponent } from './complete-registration/complete-registration.component';
import { VerifyMobileNumberComponent } from './verify-mobile-number/verify-mobile-number.component';
import { CreatePasswordComponent } from './create-password/create-password.component';
import { SecurityQuestionComponent } from './security-question/security-question.component';
import { RegisterUserConfirmationComponent } from './register-user-confirmation/register-user-confirmation.component';
import { MatProgressBar } from '@angular/material/progress-bar';

@Component({
	selector: 'app-register-user',
	templateUrl: './register-user.component.html',
	styleUrls: ['./register-user.component.scss', '../common/main.scss'],
	imports: [
		MatCard,
		NgClass,
		MatStepper,
		MatStep,
		MatStepContent,
		CompleteRegistrationComponent,
		VerifyMobileNumberComponent,
		CreatePasswordComponent,
		SecurityQuestionComponent,
		RegisterUserConfirmationComponent,
		MatProgressBar,
	],
})
export class RegisterUserComponent implements OnInit, AfterViewInit {
	@ViewChild('stepper') stepper!: MatStepper;
	private readonly destroyRef = inject(DestroyRef);
	@Output() registerUserRequestStatusEvents =
		new EventEmitter<RegisterUserRequestStatusEvent>();

	token = '';
	registerUserData: RegisterUserData = {
		email: '',
		firstName: '',
		lastName: '',
		assignedRoleName: '',
		assignedCompanyName: '',
		mobilePhoneNumber: undefined,
		token: '',
		userId: '',
		securityQuestion: '',
		securityAnswer: '',
	};

	initialStep = '1';
	initialProggressValue = 10;
	showRegistrationComplete = false;
	showVerifyMobilePhoneNumber = true;

	validUserPassword = '';
	userId = '';
	securityQuestion = '';
	securityAnswer = '';

	constructor(
		private readonly activeRoute: ActivatedRoute,
		private readonly changeDetectorRef: ChangeDetectorRef,
		private readonly dialog: MatDialog,
		private readonly router: Router,
		private readonly apiService: ApiService,
		private readonly authService: AuthService
	) {}

	ngAfterViewInit(): void {
		this.stepper.selectionChange
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.refreshStepperView());

		this.stepper.animationDone
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.refreshStepperView());
	}

	private refreshStepperView(): void {
		this.changeDetectorRef.detectChanges();
		requestAnimationFrame(() => {
			const currentStep = document.querySelector(
				'.mat-horizontal-stepper-content-current'
			);
			if (currentStep instanceof HTMLElement) {
				// Force layout reflow — Safari can leave step content at zero visible size until resize.
				void currentStep.offsetHeight;
			}
			this.changeDetectorRef.detectChanges();
		});
	}

	ngOnInit(): void {
		const token: string | null =
			this.activeRoute.snapshot.queryParamMap.get('token');
		this.showTokenValidationUi(token).then(() => {});
	}

	get progressBarValue(): number {
		return this.initialProggressValue;
	}

	private async showTokenValidationUi(token: string | null): Promise<void> {
		if (token === null || token.length === 0) {
			const dialog = this.dialog.open(AppDialogComponent, {
				data: {
					title: $localize`:@@createPassordInvalidVerificationLink:Invalid verification link`,
					message: $localize`:@@createPasswordPleaseRequestNewVerificationLink:Please request a new verification email.`,
				},
			});
			lastValueFrom(dialog.afterClosed()).then(() =>
				this.router.navigateByUrl('/login')
			);
		} else {
			this.token = token;
			const tokenRecord =
				await this.authService.checkUserRegistrationTokenStatus(token);
			if (!tokenRecord.valid) {
				const dialog = this.dialog.open(AppDialogComponent, {
					data: {
						title: $localize`:@@createPassordInvalidVerificationLink:Invalid verification link`,
						message: $localize`:@@createPasswordLinkNotValid:The link has expired or was not valid. Please request a new verification email.`,
					},
				});
				lastValueFrom(dialog.afterClosed()).then(() =>
					this.router.navigateByUrl('/login')
				);
			}
			this.userId = tokenRecord.tokenRecord.userId;
			const userAndRoleData = await this.apiService.getUserAndRole(
				token,
				tokenRecord.tokenRecord.userId
			);
			this.registerUserData = {
				email: userAndRoleData.user.email,
				firstName: userAndRoleData.user.firstName,
				lastName: userAndRoleData.user.lastName,
				assignedRoleName: userAndRoleData.roleName,
				assignedCompanyName: userAndRoleData.roleCompanyName,
				token: tokenRecord.tokenRecord.token,
				mobilePhoneNumber: userAndRoleData.user.phoneNumber,
				userId: tokenRecord.tokenRecord.userId,
				securityQuestion: this.securityQuestion,
				securityAnswer: this.securityAnswer,
			};
		}
	}

	onUpdateStepperDetails(stepperDetails: StepperDetails): void {
		this.initialProggressValue = parseInt(stepperDetails.stepProgress, 10);
		this.initialStep = stepperDetails.stepCount;
	}

	onShowMobileVerificationScreen(showMobileVerificationScreen: boolean): void {
		this.showVerifyMobilePhoneNumber = showMobileVerificationScreen;
	}

	onUpdateRegisterUserData(registerUserData: RegisterUserData): void {
		this.registerUserData = registerUserData;
	}

	onUserPasswordValid(password: string): void {
		this.validUserPassword = password;
	}

	onSecurityQuestionValid(question: string): void {
		this.registerUserData.securityQuestion = question;
	}

	onSecurityAnswerValid(answer: string): void {
		this.registerUserData.securityAnswer = answer;
	}

	async submitRegisterUserData(): Promise<void> {
		this.showRegistrationComplete = true;
		this.registerUserRequestStatusEvents.emit({
			status: RegisterUserRequestStatus.loading,
		});

		try {
			await this.authService
				.completeNewUserRegistration(
					this.validUserPassword,
					this.token,
					this.userId,
					this.registerUserData.firstName,
					this.registerUserData.lastName,
					this.registerUserData.securityQuestion,
					this.registerUserData.securityAnswer
				)
				.then((_) => {
					this.registerUserRequestStatusEvents.emit({
						status: RegisterUserRequestStatus.success,
					});
				})
				.catch((_) => {
					this.registerUserRequestStatusEvents.emit({
						status: RegisterUserRequestStatus.failure,
					});
				});
		} catch (error) {
			this.registerUserRequestStatusEvents.emit({
				status: RegisterUserRequestStatus.failure,
			});
		}
	}
}

export type StepperDetails = {
	stepProgress: string;
	stepCount: string;
};

export type RegisterUserData = {
	email: string;
	firstName: string;
	lastName: string;
	assignedRoleName: string;
	assignedCompanyName: string;
	mobilePhoneNumber: PhoneNumber | undefined;
	token: string;
	userId: string;
	securityQuestion: string;
	securityAnswer: string;
};

export enum RegisterUserRequestStatus {
	loading,
	success,
	failure,
}

export interface RegisterUserRequestStatusEvent {
	status: RegisterUserRequestStatus;
	registerUserData?: RegisterUserData;
	password?: string;
}
